import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PawPrint } from 'lucide-react';

import { authApi } from '../../../api/auth.js';
import { ApiError } from '../../../api/client.js';
import { env } from '../../../lib/env.js';

/**
 * Cross-device kiosk login landing. The customer reaches this page by scanning
 * the QR shown on the in-store kiosk (/k/:token). It exchanges the single-use
 * grant token for the email of the linked account, then sends the customer to
 * the normal login form with that email pre-filled. No session is granted by the
 * QR itself: the customer still authenticates with password and, if enabled,
 * MFA/passkey, so a photographed QR cannot grant access.
 */
export function KioskLoginPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(token ? 'working' : 'error'); // working | error
  const attempted = useRef(false);

  useEffect(() => {
    // Single-use token: guard against the effect firing twice (React strict
    // mode) which would consume the grant once and then fail the second call.
    if (attempted.current || !token) return;
    attempted.current = true;
    authApi.kioskResolve({ token })
      .then((data) => {
        // Land on the standard login with the email pre-filled. The password
        // and any MFA/passkey step run through the normal flow.
        navigate('/login', { replace: true, state: { email: (data && data.email) || '' } });
      })
      .catch((err) => {
        if (!(err instanceof ApiError)) { /* network */ }
        setStatus('error');
      });
  }, [token, navigate]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-portal-bg px-6 text-center">
      <div className="w-full max-w-sm">
        <p className="text-xs font-semibold uppercase tracking-wider text-portal-ink-faint">{env.appName}</p>
        <PawPrint className="mx-auto mt-6 h-12 w-12 text-portal-brand" strokeWidth={1.5} />
        <h1 className="mt-4 text-2xl font-semibold text-portal-ink">Accesso in corso</h1>

        {status === 'working' && (
          <p className="mt-3 text-portal-ink-muted">Ti porto al tuo accesso…</p>
        )}

        {status === 'error' && (
          <>
            <p className="mt-3 text-portal-ink-muted">
              Il codice di accesso non è più valido. Chiedi all'assistente in negozio di generarne uno nuovo.
            </p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="mt-6 text-sm text-portal-ink-muted hover:text-portal-brand"
            >
              Accedi con email e password
            </button>
          </>
        )}
      </div>
    </div>
  );
}
